import {
  reactive,
  readonly,
  watchEffect,
  onScopeDispose,
  toValue,
  type MaybeRefOrGetter,
  type DeepReadonly,
} from 'vue'
import type { RouteLocationRaw } from 'vue-router'

/**
 * App Chrome v1 — view-owned top chrome state.
 *
 * A view declares the *shape* of its chrome statically in route meta
 * (`meta.chrome`), and supplies the *dynamic* scalars (title, ascent target,
 * etc.) at runtime through this composable. Stateful tool subtrees are NOT
 * routed through here — those are projected into the shell via
 * `<Teleport to="#app-chrome-tools">` so they stay owned by and reactive within
 * the view. This composable carries only the layout-driving scalars.
 *
 * Backed by a module-level `reactive()` singleton: this is UI-ephemeral state
 * (one chrome exists at a time, owned by whichever view is mounted), not domain
 * data, so it deliberately lives outside Pinia. The shell reads it via
 * `usePageChromeState()`.
 */

export type ChromeVariant = 'hidden' | 'header' | 'tools' | 'header+tools'

/**
 * A type-level lateral nav link rendered in the chrome's links region. Mirrors
 * the `ChromeLink` shape declared for `meta.links` so static and runtime links
 * share one render path. Curated, schema-drawable, sideways — not ascent.
 */
export interface ChromeLink {
  to: RouteLocationRaw
  label: string
  iconName?: 'characters' | 'conversations' | 'lorebooks' | 'worlds' | 'personas' | 'creator'
}

interface PageChromeState {
  /** Runtime override for the route-meta variant. `null` => defer to meta. */
  variant: ChromeVariant | null
  title: string | null
  subtitle: string | null
  /** Runtime-resolved ascent destination; beats any static `meta.ascent`. */
  ascentTo: RouteLocationRaw | null
  /** Tri-state: `null` => no opinion (shell decides from meta/parent presence). */
  ascentEnabled: boolean | null
  /** Runtime-resolved lateral links; beats any static `meta.links`. */
  links: ChromeLink[] | null
}

const defaultState = (): PageChromeState => ({
  variant: null,
  title: null,
  subtitle: null,
  ascentTo: null,
  ascentEnabled: null,
  links: null,
})

const state = reactive<PageChromeState>(defaultState())

// Identifies the view instance that currently owns the chrome. A late-running
// reset (e.g. an outgoing view's scope disposing after the incoming view has
// already claimed ownership) must not clobber the new owner's state.
let ownerToken = 0

function resetState(): void {
  Object.assign(state, defaultState())
}

type ChromeOption<T> = MaybeRefOrGetter<T>

export interface PageChromeOptions {
  variant?: ChromeOption<ChromeVariant | null | undefined>
  title?: ChromeOption<string | null | undefined>
  subtitle?: ChromeOption<string | null | undefined>
  ascentTo?: ChromeOption<RouteLocationRaw | null | undefined>
  ascentEnabled?: ChromeOption<boolean | null | undefined>
  links?: ChromeOption<ChromeLink[] | null | undefined>
}

export interface PageChromeHandle {
  setTitle(value: string | null): void
  setSubtitle(value: string | null): void
  setAscent(to: RouteLocationRaw | null): void
  setVariant(variant: ChromeVariant | null): void
  setLinks(links: ChromeLink[] | null): void
}

/**
 * Called by a view in `setup`. Accepts refs/getters so live values (e.g. a
 * character name loaded async) keep flowing into the chrome reactively. Cleans
 * up on scope teardown, so an unmounting view leaves no stale title behind.
 */
export function usePageChrome(options: PageChromeOptions = {}): PageChromeHandle {
  const token = ++ownerToken

  // Push declarative options into the singleton, re-running whenever any
  // ref/getter source changes. A `null`/`undefined` option leaves its default.
  watchEffect(() => {
    if (token !== ownerToken) return
    if (options.variant !== undefined) state.variant = toValue(options.variant) ?? null
    if (options.title !== undefined) state.title = toValue(options.title) ?? null
    if (options.subtitle !== undefined) state.subtitle = toValue(options.subtitle) ?? null
    if (options.ascentTo !== undefined) state.ascentTo = toValue(options.ascentTo) ?? null
    if (options.ascentEnabled !== undefined) {
      state.ascentEnabled = toValue(options.ascentEnabled) ?? null
    }
    if (options.links !== undefined) state.links = toValue(options.links) ?? null
  })

  onScopeDispose(() => {
    // Only clear if we're still the active owner — guards against out-of-order
    // teardown under <Transition mode="out-in">.
    if (token === ownerToken) resetState()
  })

  return {
    setTitle: (value) => {
      state.title = value
    },
    setSubtitle: (value) => {
      state.subtitle = value
    },
    setAscent: (to) => {
      state.ascentTo = to
    },
    setVariant: (variant) => {
      state.variant = variant
    },
    setLinks: (links) => {
      state.links = links
    },
  }
}

/** Read-only view of the chrome singleton — consumed by the shell. */
export function usePageChromeState(): DeepReadonly<PageChromeState> {
  return readonly(state)
}

/**
 * Route changes update route meta before the outgoing component has unmounted.
 * Clear runtime ownership at that boundary so old dynamic title/ascent/tools do
 * not override the incoming route's static chrome during the transition.
 */
export function clearPageChromeForRouteChange(): void {
  ownerToken += 1
  resetState()
}

/** Test-only escape hatch to restore the singleton between cases. */
export function __resetPageChromeForTests(): void {
  ownerToken = 0
  resetState()
}
